import type { MediaMetadata, MediaNativeSize } from './media-metadata-types';
import { sha256HexOfBlob } from './sha256';

export const gifMimeType = 'image/gif';

export const isGifMimeType = (mimeType: string | null | undefined) =>
  mimeType === gifMimeType;

/**
 * GIF header:
 * - 0-5: signature + version (GIF87a / GIF89a)
 * - 6-7: logical screen width  (little-endian)
 * - 8-9: logical screen height (little-endian)
 * @see https://www.fileformat.info/format/gif/egff.htm
 */
export async function getGifNativeSize(blob: Blob): Promise<MediaNativeSize | null> {
  try {
    const headerBuffer = await blob.slice(0, 10).arrayBuffer();
    const bytes = new Uint8Array(headerBuffer);
    if (bytes.length < 10) {
      return null;
    }

    const signature = String.fromCharCode(...bytes.slice(0, 6));
    if (signature !== 'GIF87a' && signature !== 'GIF89a') {
      return null;
    }

    const width = bytes[6] | (bytes[7] << 8);
    const height = bytes[8] | (bytes[9] << 8);

    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return null;
    }

    return { x: width, y: height };
  } catch {
    return null;
  }
}

export async function getGifMetadataFromBlob(input: {
  blob: Blob;
  mimeType: string;
}): Promise<MediaMetadata | null> {
  const nativeSize = await getGifNativeSize(input.blob);
  if (!nativeSize) {
    return null;
  }

  const dpr = globalThis.devicePixelRatio ?? 1;

  const size = {
    x: Math.round(nativeSize.x / dpr),
    y: Math.round(nativeSize.y / dpr),
  };

  const hash = await sha256HexOfBlob(input.blob);

  return {
    mimeType: input.mimeType,
    nativeSize,
    size,
    // This forces GIFs to use DOM rendering instead of WebGL rendering.
    // There was a bug where if the first frame of a gif was transparent, it would be rendered as transparent image and didn't animate.
    isTransparent: false,
    hash,
  };
}
